import React, { useEffect, useRef } from 'react';
import Typed from 'typed.js';
import SEO from "../../common/SEO";
import HeaderOne from '../../common/header/HeaderOne';
import HeaderTopBar from '../../common/header/HeaderTopBar';
import FooterTwo from '../../common/footer/FooterTwo';
import Copyright from '../../common/footer/Copyright';
import Separator from "../separator/Separator";
import SectionTitle from "../sectionTitle/SectionTitle";
import { 
    FiCheck, FiSmartphone, FiMessageCircle, FiShield, FiClock, FiArrowRight, 
    FiCpu, FiZap, FiGlobe, FiActivity, FiTarget, FiCode,
    FiAlertCircle, FiXCircle, FiFacebook, FiUser, FiMoon, FiAlertTriangle,
    FiAperture, FiImage, FiVideo, FiFileText, FiLink, FiDatabase, FiLayers,
    FiSearch, FiBarChart2, FiPenTool, FiGrid, FiTrendingUp, FiGitBranch, FiTag
} from "react-icons/fi";
import CountUp from 'react-countup';
import TrackVisibility from "react-on-screen";
import ScrollAnimation from "react-animate-on-scroll";
import { Link } from "react-router-dom";

const FeaturesData = [
    {
        icon: <FiAperture />,
        title: 'Multimodal Fusion',
        description: 'Seamlessly combines text, image, video, and audio data for holistic understanding.',
        number: '01'
    },
    {
        icon: <FiImage />,
        title: 'Visual Intelligence',
        description: 'Advanced computer vision detects objects, scenes, and relationships in images.',
        number: '02'
    },
    {
        icon: <FiVideo />,
        title: 'Video Analysis',
        description: 'Frame by frame processing with temporal understanding and action recognition.',
        number: '03'
    },
    {
        icon: <FiFileText />,
        title: 'Text Generation',
        description: 'Context aware, brand aligned text generation from any input modality.',
        number: '04'
    },
    {
        icon: <FiSearch />,
        title: 'Cross Modal Search',
        description: 'Search any content type using natural language queries.',
        number: '05'
    },
    {
        icon: <FiDatabase />,
        title: 'Knowledge Graph',
        description: 'Connected information system with deep semantic understanding.',
        number: '06'
    }
];

const CounterData = [
    {
        countNum : 99,
        countTitle: 'Accuracy Rate',
        suffix: '%'
    },
    {
        countNum : 50,
        countTitle: 'Languages Supported',
        suffix: '+'
    },
    {
        countNum : 10,
        countTitle: 'Modality Types',
        suffix: ''
    },
    {
        countNum : 24,
        countTitle: 'Hours Available',
        suffix: '/7'
    }
];

const ProcessData = [
    {
        step: 'Phase 01',
        title: 'Data Integration',
        description: 'Connect all your data sources into a unified multimodal pipeline.'
    },
    {
        step: 'Phase 02',
        title: 'Model Training',
        description: 'Customize the AI to understand your specific domain and use cases.'
    },
    {
        step: 'Phase 03',
        title: 'Deployment',
        description: 'Launch your multimodal AI solution with enterprise grade security.'
    }
];

const ChallengeData = [
    {
        icon: <FiAlertTriangle />,
        title: 'Data Silos',
        description: 'Different data types stored separately prevent holistic understanding.'
    },
    {
        icon: <FiXCircle />,
        title: 'Limited Context',
        description: 'Single modal systems miss the full picture by only processing one data type.'
    },
    {
        icon: <FiClock />,
        title: 'Slow Processing',
        description: 'Manual processing of multiple data types is time consuming and error prone.'
    }
];

const SolutionData = [
    {
        icon: <FiLayers />,
        title: 'Unified Platform',
        description: 'All your data types processed together in a single intelligent system.'
    },
    {
        icon: <FiCpu />,
        title: 'High Performance',
        description: 'Distributed computing handles large scale multimodal processing with ease.'
    },
    {
        icon: <FiZap />,
        title: 'Real-time Insights',
        description: 'Get instant analysis and insights from all your data sources simultaneously.'
    }
];

const DeepDiveData = [
    {
        icon: <FiLayers />,
        title: 'React + Three.js',
        description: 'Interactive 3D graph frontend for visualizing data relationships'
    },
    {
        icon: <FiActivity />,
        title: 'FastAPI Backend',
        description: 'FastAPI backend + ADK Runner for agent orchestration'
    },
    {
        icon: <FiCpu />,
        title: 'Vertex AI',
        description: 'Vertex AI Agent Engine + Memory Bank for intelligence'
    },
    {
        icon: <FiDatabase />,
        title: 'Cloud Spanner',
        description: 'Cloud Spanner Graph + Gemini embeddings for data storage'
    }
];

const MultimodelAiAgent = () => {
    const el = useRef(null);

    useEffect(() => {
        const typed = new Typed(el.current, {
            strings: ["Input Agent", "AI Powered Community Agent"],
            typeSpeed: 80,
            backSpeed: 60,
            backDelay: 2000,
            startDelay: 500,
            loop: true
        });

        return () => {
            typed.destroy();
        };
    }, []);

    return (
        <>
            <style>
                {`
                    .hero-title-container {
                        height: 300px;
                        margin-bottom: 5px;
                    }
                    @media (max-width: 768px) {
                        .hero-title-container {
                            height: 400px;
                        }
                    }
                    @media (max-width: 480px) {
                        .hero-title-container {
                            height: 450px;
                        }
                    }
                .erp-hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    background: linear-gradient(135deg, rgba(101, 73, 213, 0.15), rgba(5, 157, 255, 0.15));
                    padding: 12px 26px;
                    border-radius: 40px;
                    position: relative;
                    margin-bottom: 10px;
                    margin-top: 20px;
                }
                .erp-hero-badge::before {
                    content: '';
                    position: absolute;
                    top: -1px;
                    left: -1px;
                    right: -1px;
                    bottom: -1px;
                    border-radius: 40px;
                    background: linear-gradient(90deg, #6549D5, #059DFF, #E33FA1);
                    z-index: -1;
                }
                .erp-hero-badge::after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border-radius: 40px;
                    background: linear-gradient(135deg, rgba(101, 73, 213, 0.15), rgba(5, 157, 255, 0.15));
                    z-index: -1;
                }
                .erp-hero-badge span {
                    font-size: 14px;
                    font-weight: 700;
                    background: linear-gradient(90deg, #6549D5, #059DFF);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                .slider-area .thumbnail img {
                    border-radius: 30px;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15);
                    border: 1px solid rgba(255,255,255,0.05);
                }
                .slider-area .title {
                    min-height: auto !important;
                    margin-top: 0 !important;
                    margin-bottom: 15px !important;
                }
                .slider-area .description {
                    margin-bottom: 25px !important;
                }
                .slider-area .inner {
                    margin-top: 0 !important;
                }
                .slider-area.height-750 {
                    min-height: auto !important;
                    height: auto !important;
                    padding: 100px 0 40px 0 !important;
                }
                .main-content {
                    padding-top: 0 !important;
                }
                    .feature-card-alt {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.08), rgba(5, 157, 255, 0.08));
                        border: 1px solid rgba(101, 73, 213, 0.15);
                    }
                    .deep-dive-card {
                        background: linear-gradient(135deg, #1a1a2e, #16213e);
                        border: 1px solid rgba(101, 73, 213, 0.2);
                        border-radius: 25px;
                        padding: 45px 35px;
                        height: 100%;
                        transition: all 0.3s ease;
                        position: relative;
                    }
                    .timeline-connector {
                        display: none;
                    }
                    @media (min-width: 992px) {
                        .timeline-connector {
                            display: block;
                            position: absolute;
                            top: 60px;
                            right: -30px;
                            width: 60px;
                            height: 2px;
                            background: linear-gradient(90deg, #6549D5, #059DFF);
                        }
                    }

                    /* Custom classes for Multimodal AI Agent page with light mode support */
                    .mmai-pill {
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        background: linear-gradient(90deg, rgba(101, 73, 213, 0.2), rgba(5, 157, 255, 0.2));
                        padding: 10px 24px;
                        border-radius: 30px;
                        border: 1px solid rgba(101, 73, 213, 0.3);
                    }
                    .mmai-pill-dot {
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        background: #6549D5;
                        box-shadow: 0 0 15px #6549D5;
                        animation: pulse 2s infinite;
                    }
                    .mmai-pill-text {
                        font-size: 14px;
                        font-weight: 700;
                        background: linear-gradient(90deg, #6549D5, #059DFF);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                    }
                    .mmai-description {
                        color: #acacac;
                    }
                    .mmai-section {
                        background: linear-gradient(135deg, rgba(101,73,213,0.03), rgba(5,157,255,0.03));
                        border-radius: 40px;
                        padding: 80px 30px;
                        margin: 0 20px;
                    }
                    .mmai-section-dark {
                        background: linear-gradient(135deg, rgba(15, 15, 17, 0.8), rgba(26, 26, 26, 0.9));
                        border-radius: 40px;
                        padding: 60px 30px;
                        margin: 0 20px;
                    }
                    .mmai-step-card {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.15), rgba(101, 73, 213, 0.08));
                        border-radius: 20px;
                        padding: 25px 30px;
                        border: 1px solid rgba(101, 73, 213, 0.2);
                        height: 100%;
                    }
                    .mmai-step-number {
                        width: 50px;
                        height: 50px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, #6549D5, #4A3CA8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 18px;
                        font-weight: 800;
                        color: #fff;
                    }
                    .mmai-step-title {
                        color: #fff;
                    }
                    .mmai-step-desc {
                        color: #acacac;
                    }
                    .mmai-timeline-card {
                        background: linear-gradient(135deg, rgba(15,15,17,0.95), rgba(26,26,26,0.95));
                        padding: 50px 35px;
                        border-radius: 30px;
                        height: 100%;
                        border: 1px solid rgba(101,73,213,0.15);
                    }
                    .mmai-timeline-number {
                        width: 70px;
                        height: 70px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, #6549D5, #059DFF);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: 800;
                        color: #fff;
                        box-shadow: 0 10px 30px rgba(101,73,213,0.3);
                    }
                    .mmai-timeline-step {
                        display: inline-block;
                        background: linear-gradient(135deg, rgba(101,73,213,0.2), rgba(5,157,255,0.2));
                        padding: 8px 18px;
                        border-radius: 12px;
                        font-size: 12px;
                        font-weight: 700;
                        color: #6549D5;
                    }
                    .mmai-timeline-title {
                        color: #fff;
                    }
                    .mmai-timeline-desc {
                        color: #acacac;
                    }
                    .mmai-key-outcomes-section {
                        background: linear-gradient(135deg, #0f0f11, #1a1a2e);
                        border-radius: 40px;
                        padding: 80px 30px;
                        margin: 0 20px;
                        position: relative;
                        overflow: hidden;
                    }
                    .mmai-key-outcomes-bg {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: radial-gradient(circle at 30% 20%, rgba(101, 73, 213, 0.2) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(227, 63, 161, 0.2) 0%, transparent 50%);
                        z-index: 0;
                    }
                    .mmai-key-outcomes-title {
                        font-size: 48px;
                        color: #fff;
                        font-weight: 700;
                        margin-bottom: 50px;
                        background: linear-gradient(90deg, #fff, #d4cce8);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                    }
                    .mmai-key-outcomes-card {
                        background: linear-gradient(135deg, rgba(58, 43, 125, 0.9), rgba(26, 14, 69, 0.9));
                        padding: 35px 30px;
                        border-radius: 12px;
                        height: 100%;
                        border: 1px solid rgba(101, 73, 213, 0.4);
                        transition: all 0.3s ease;
                    }
                    .mmai-key-outcomes-card:hover {
                        transform: translateY(-8px);
                        box-shadow: 0 20px 40px rgba(101,73,213,0.25);
                        border-color: rgba(101, 73, 213, 0.7);
                    }
                    .mmai-key-outcomes-card-title {
                        color: #fff;
                        font-size: 28px;
                        font-weight: 700;
                        margin-bottom: 12px;
                        letter-spacing: 0.5px;
                    }
                    .mmai-key-outcomes-card-desc {
                        color: #d4cce8;
                        font-size: 18px;
                        line-height: 1.6;
                    }
                    .mmai-embeddings-title {
                        color: #fff;
                    }
                    .mmai-embeddings-desc {
                        color: #acacac;
                    }
                    .mmai-embeddings-problem-title {
                        color: #fff;
                    }
                    .mmai-embeddings-problem-desc {
                        color: #acacac;
                    }
                    .mmai-embeddings-success {
                        background: linear-gradient(135deg, rgba(46, 125, 50, 0.2), rgba(76, 175, 80, 0.1));
                        border: 1px solid rgba(76, 175, 80, 0.3);
                        border-radius: 12px;
                        padding: 20px 25px;
                        display: flex;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .mmai-embeddings-success-icon {
                        color: #4CAF50;
                        font-size: 20px;
                        margin-top: 3px;
                        flex-shrink: 0;
                    }
                    .mmai-embeddings-success-text strong {
                        color: #fff;
                        font-size: 15px;
                    }
                    .mmai-embeddings-success-text span {
                        color: #acacac;
                        font-size: 15px;
                    }
                    .mmai-graph-rag-title {
                        color: #fff;
                    }
                    .mmai-graph-rag-item-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 10px;
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.2), rgba(5, 157, 255, 0.15));
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #6549D5;
                        flex-shrink: 0;
                    }
                    .mmai-graph-rag-item-title {
                        color: #fff;
                        font-size: 18px;
                        font-weight: 700;
                        margin-bottom: 5px;
                    }
                    .mmai-graph-rag-item-desc {
                        color: #acacac;
                        font-size: 14px;
                        line-height: 1.6;
                    }
                    .mmai-memory-card {
                        background: linear-gradient(135deg, #3a2b7d, #1a0e45);
                        padding: 35px 30px;
                        border-radius: 12px;
                        height: 100%;
                        border: 1px solid rgba(101, 73, 213, 0.3);
                        transition: all 0.3s ease;
                    }
                    .mmai-memory-card:hover {
                        transform: translateY(-10px);
                        box-shadow: 0 20px 40px rgba(101,73,213,0.2);
                    }
                    .mmai-memory-icon {
                        width: 60px;
                        height: 60px;
                        border-radius: 15px;
                        background: rgba(255,255,255,0.1);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 26px;
                        color: #fff;
                        margin-bottom: 20px;
                    }
                    .mmai-memory-title {
                        color: #fff;
                        font-size: 26px;
                        font-weight: 700;
                        margin-bottom: 12px;
                    }
                    .mmai-memory-desc {
                        color: #d4cce8;
                        font-size: 17px;
                        line-height: 1.6;
                    }
                    .mmai-memory-section-title {
                        color: #fff;
                    }
                    .mmai-sequential-title {
                        color: #fff;
                        font-size: 22px;
                        font-weight: 700;
                        margin-bottom: 20px;
                    }
                    .mmai-sequential-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 10px;
                        color: #acacac;
                        font-size: 15px;
                        line-height: 1.6;
                    }
                    .mmai-sequential-dot {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: #6549D5;
                        margin-top: 8px;
                        flex-shrink: 0;
                    }
                    .mmai-deep-dive-icon {
                        width: 70px;
                        height: 70px;
                        border-radius: 20px;
                        background: linear-gradient(135deg, rgba(101,73,213,0.2), rgba(5,157,255,0.2));
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 25px;
                        margin-left: auto;
                        margin-right: auto;
                    }
                    .mmai-deep-dive-icon svg {
                        font-size: 30px;
                        color: #6549D5;
                    }
                    .mmai-deep-dive-title {
                        color: #fff;
                        font-size: 22px;
                        font-weight: 700;
                        margin-bottom: 15px;
                    }
                    .mmai-deep-dive-desc {
                        color: #acacac;
                        font-size: 15px;
                        line-height: 1.7;
                    }
                    .mmai-feature-card {
                        background: linear-gradient(135deg, #0f0f11, #1a1a1a);
                        padding: 45px 35px;
                        border-radius: 25px;
                        height: 100%;
                        border: 1px solid rgba(5, 157, 255, 0.1);
                        transition: all 0.3s ease;
                        position: relative;
                        overflow: hidden;
                    }
                    .mmai-feature-card:hover {
                        transform: translateY(-10px);
                        border-color: rgba(101, 73, 213, 0.3);
                        box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 40px rgba(101, 73, 213, 0.1);
                    }
                    .mmai-feature-card-alt {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.08), rgba(5, 157, 255, 0.08));
                        border: 1px solid rgba(101, 73, 213, 0.15);
                    }
                    .mmai-feature-card-alt:hover {
                        border-color: rgba(101, 73, 213, 0.3);
                    }
                    .mmai-counter-number {
                        color: #fff;
                    }
                    .mmai-counter-title {
                        color: #fff;
                    }
                    .mmai-challenge-title {
                        color: #fff;
                    }
                    .mmai-challenge-desc {
                        color: #acacac;
                    }
                    .mmai-solution-title {
                        color: #fff;
                    }
                    .mmai-solution-desc {
                        color: #acacac;
                    }
                    .mmai-cta-container {
                        background: linear-gradient(135deg, #6549D5 0%, #059DFF 35%, #E33FA1 100%); 
                        padding: 80px 50px;
                        border-radius: 50px;
                        text-align: center;
                        position: relative;
                        overflow: hidden;
                    }
                    .mmai-cta-bg {
                        position: absolute; 
                        top: -50%; 
                        left: -50%; 
                        width: 200%; 
                        height: 200%; 
                        background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%);
                        animation: rotate 25s linear infinite;
                    }
                    .mmai-cta-content {
                        position: relative;
                        z-index: 1;
                    }
                    .mmai-cta-title {
                        font-size: 42px; 
                        color: #fff; 
                        margin-bottom: 20px; 
                        font-weight: 800;
                    }
                    .mmai-cta-desc {
                        font-size: 20px; 
                        color: rgba(255,255,255,0.95); 
                        margin-bottom: 35px; 
                        max-width: 700px; 
                        margin-left: auto; 
                        margin-right: auto;
                    }


                    /* LIGHT MODE OVERRIDES */
                    body.light .mmai-description {
                        color: #65676b;
                    }
                    body.light .mmai-section {
                        background: linear-gradient(135deg, rgba(101,73,213,0.05), rgba(5,157,255,0.05));
                    }
                    body.light .mmai-section-dark {
                        background: linear-gradient(135deg, rgba(245,248,250,0.9), rgba(233,248,254,0.9));
                    }
                    body.light .mmai-step-card {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.1), rgba(5, 157, 255, 0.05));
                    }
                    body.light .mmai-step-title {
                        color: #181818;
                    }
                    body.light .mmai-step-desc {
                        color: #65676b;
                    }
                    body.light .mmai-timeline-card {
                        background: #f5f8fa;
                    }
                    body.light .mmai-timeline-title {
                        color: #181818;
                    }
                    body.light .mmai-timeline-desc {
                        color: #65676b;
                    }
                    body.light .mmai-key-outcomes-section {
                        background: linear-gradient(135deg, #f5f8fa, #e9f8fe);
                    }
                    body.light .mmai-key-outcomes-bg {
                        background: radial-gradient(circle at 30% 20%, rgba(101, 73, 213, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(227, 63, 161, 0.1) 0%, transparent 50%);
                    }
                    body.light .mmai-key-outcomes-title {
                        background: linear-gradient(90deg, #181818, #6549D5);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                        background-clip: text;
                    }
                    body.light .mmai-key-outcomes-card {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.1), rgba(5, 157, 255, 0.05));
                    }
                    body.light .mmai-key-outcomes-card-title {
                        color: #181818;
                    }
                    body.light .mmai-key-outcomes-card-desc {
                        color: #65676b;
                    }
                    body.light .mmai-embeddings-title {
                        color: #181818;
                    }
                    body.light .mmai-embeddings-desc {
                        color: #65676b;
                    }
                    body.light .mmai-embeddings-problem-title {
                        color: #181818;
                    }
                    body.light .mmai-embeddings-problem-desc {
                        color: #65676b;
                    }
                    body.light .mmai-embeddings-success {
                        background: linear-gradient(135deg, rgba(46, 125, 50, 0.1), rgba(76, 175, 80, 0.05));
                    }
                    body.light .mmai-embeddings-success-text strong {
                        color: #181818;
                    }
                    body.light .mmai-embeddings-success-text span {
                        color: #65676b;
                    }
                    body.light .mmai-graph-rag-title {
                        color: #181818;
                    }
                    body.light .mmai-graph-rag-item-title {
                        color: #181818;
                    }
                    body.light .mmai-graph-rag-item-desc {
                        color: #65676b;
                    }
                    body.light .mmai-memory-card {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.1), rgba(5, 157, 255, 0.05));
                    }
                    body.light .mmai-memory-icon {
                        background: rgba(101, 73, 213, 0.1);
                    }
                    body.light .mmai-memory-icon svg {
                        color: #6549D5;
                    }
                    body.light .mmai-memory-title {
                        color: #181818;
                    }
                    body.light .mmai-memory-desc {
                        color: #65676b;
                    }
                    body.light .mmai-memory-section-title {
                        color: #181818;
                    }
                    body.light .mmai-sequential-title {
                        color: #181818;
                    }
                    body.light .mmai-sequential-item {
                        color: #65676b;
                    }
                    body.light .deep-dive-card {
                        background: linear-gradient(135deg, #f5f8fa, #e9f8fe);
                    }
                    body.light .mmai-deep-dive-title {
                        color: #181818;
                    }
                    body.light .mmai-deep-dive-desc {
                        color: #65676b;
                    }

                    body.light .mmai-feature-card {
                        background: #f5f8fa;
                    }
                    body.light .mmai-feature-card-alt {
                        background: linear-gradient(135deg, rgba(101, 73, 213, 0.08), rgba(5, 157, 255, 0.08));
                    }
                    body.light .mmai-counter-number {
                        color: #181818;
                    }
                    body.light .mmai-counter-title {
                        color: #181818;
                    }
                    body.light .mmai-challenge-title {
                        color: #181818;
                    }
                    body.light .mmai-challenge-desc {
                        color: #65676b;
                    }
                    body.light .mmai-solution-title {
                        color: #181818;
                    }
                    body.light .mmai-solution-desc {
                        color: #65676b;
                    }
                `}
            </style>
            <SEO 
                title="WhatsOn Multimodal AI Agent"
                openGraph={{
                    title: "WhatsOn Multimodal AI Agent",
                    description: "AI that understands text, images, videos, and audio together. Unlock unified intelligence with our multimodal system.",
                    url: "/multimodal-ai-agent",
                    type: "website"
                }}
            />
            <main className="page-wrapper theme-shape">
                <HeaderTopBar />
                <HeaderOne btnStyle="round btn-icon" HeaderSTyle="header-not-transparent" />

                {/* Hero Section - AI Vibe */}
                <div className="slider-area slider-style-1 bg-transparent variation-2" style={{ paddingTop: '0px', paddingBottom: '30px' }}>
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-8 order-2 order-lg-1 mt_md--40 mt_sm--40">
                                <div className="inner text-start" style={{ paddingTop: '0px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                        <div style={{ 
                                            display: 'inline-flex', 
                                            alignItems: 'center', 
                                            gap: '10px', 
                                            background: 'linear-gradient(135deg, rgba(5, 157, 255, 0.15), rgba(101, 73, 213, 0.15))', 
                                            padding: '8px 20px', 
                                            borderRadius: '50px', 
                                            marginBottom: '10px',
                                            marginTop: '20px',
                                            border: '1px solid rgba(5, 157, 255, 0.2)'
                                        }}>
                                            <span style={{ 
                                                width: '8px', 
                                                height: '8px', 
                                                borderRadius: '50%', 
                                                background: '#059DFF', 
                                                boxShadow: '0 0 10px #059DFF',
                                                animation: 'pulse 2s infinite'
                                            }}></span>
                                            <span style={{ fontSize: '14px', fontWeight: '600', background: 'linear-gradient(90deg, #059DFF, #6549D5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                                                🧠 NEXT-GEN AI
                                            </span>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={150}>
                                        <div className="hero-title-container">
                                            <h2 className="title theme-gradient display-two" style={{ marginBottom: '0', height: 'auto' }}>
                                                Multimodal AI Agent
                                                {' '}
                                                <span ref={el} className="theme-gradient"></span>
                                            </h2>
                                        </div>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={300}>
                                        <p className="description" style={{ marginBottom: '', marginTop: '50px' }}>
                                            Introducing WhatsOn Multimodal community Agent: Vertex AI Memory Bank, Cloud Spanner, Gemini embeddings, React + Three.js frontend.
                                        </p>
                                    </ScrollAnimation>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={450}>
                                        <div className="button-group" aria-label="Get Contact">
                                            <Link
                                                className="btn-default btn-medium round btn-icon"
                                                to="/contact"
                                            >
                                                Contact Us
                                                <i className="icon">
                                                    <FiArrowRight />
                                                </i>
                                            </Link>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            </div>
                            <div className="col-lg-4 order-1 order-lg-2">
                                <ScrollAnimation animateIn="fadeInRight" animateOnce={true} delay={200}>
                                    <div className="thumbnail">
                                        <img
                                            src="/images/bg/MultimodelAiAgent.png"
                                            alt="Multimodal AI Agent"
                                            style={{ 
                                                borderRadius: '30px', 
                                                boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                border: '1px solid rgba(255,255,255,0.05)'
                                            }}
                                        />
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Core Architecture - Layered Structure */}
                <div className="rn-section-gap mmai-section" style={{ padding: '80px 0' }}>
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12 text-center">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="CORE ARCHITECTURE"
                                    title="Layered Technology Stack"
                                    description="A robust, scalable architecture with four distinct layers."
                                />
                            </div>
                        </div>
                        <div className="row mt--40 justify-content-center">
                            {DeepDiveData.map((item, index) => (
                                <div className="col-lg-3 col-md-6 col-sm-12 mt--30" key={index}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 120}>
                                        <div className="deep-dive-card text-center"
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-12px)';
                                            e.currentTarget.style.boxShadow = '0 25px 50px rgba(101,73,213,0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}>
                                            <div className="mmai-deep-dive-icon mx-auto">
                                                {item.icon}
                                            </div>
                                            <h4 className="mmai-deep-dive-title">{item.title}</h4>
                                            <p className="mmai-deep-dive-desc">{item.description}</p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* AI-Powered Embeddings Segment */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                    <h2 className="mmai-embeddings-title" style={{ fontSize: '36px', marginBottom: '20px' }}>
                                        AI Powered Embeddings in Spanner
                                    </h2>
                                    <p className="mmai-embeddings-desc" style={{ fontSize: '16px', lineHeight: '1.7', marginBottom: '40px' }}>
                                        Rhythmic Transmitters leverages advanced AI embeddings to revolutionize data search and analysis within Google Cloud Spanner, moving beyond traditional keyword matching.
                                    </p>
                                </ScrollAnimation>
                            </div>
                        </div>

                        {/* Problem with Keyword Search */}
                        <div className="row" style={{ marginBottom: '50px' }}>
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={100}>
                                    <div style={{ borderLeft: '4px solid #6549D5', paddingLeft: '20px', marginBottom: '20px' }}>
                                        <h3 className="mmai-embeddings-problem-title" style={{ fontSize: '26px', marginBottom: '15px' }}>
                                            The Problem with Keyword Search
                                        </h3>
                                    </div>
                                    <p className="mmai-embeddings-problem-desc" style={{ fontSize: '15px', lineHeight: '1.7', marginBottom: '25px' }}>
                                        Traditional keyword searches often miss relevant results due to synonyms, different phrasing, or conceptual gaps. For instance, searching for "dance therapy" might not find "therapeutic eurythmy" or "moon gesture" if those exact words aren't present.
                                    </p>
                                    <div className="mmai-embeddings-success">
                                        <FiCheck className="mmai-embeddings-success-icon" />
                                        <div className="mmai-embeddings-success-text">
                                            <strong>Semantic Search:</strong>
                                            <span> With embeddings, our system can understand the context and meaning of queries, successfully finding related concepts like "Therapeutic Eurythmy" and "Moon Gesture" even if the query uses different terms.</span>
                                        </div>
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>

                        {/* Implementing Embeddings Section */}
                        <div className="row" style={{ marginBottom: '20px' }}>
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={200}>
                                    <h3 className="mmai-embeddings-problem-title" style={{ fontSize: '26px', marginBottom: '15px' }}>
                                        Implementing Embeddings in Spanner
                                    </h3>
                                    <p className="mmai-embeddings-problem-desc" style={{ fontSize: '15px', lineHeight: '1.7', marginBottom: '30px' }}>
                                        Integrating AI embeddings directly into Spanner involves a straightforward four step process:
                                    </p>
                                </ScrollAnimation>
                            </div>
                        </div>
                        <div className="row row--15">
                            {[
                                {
                                    number: 1,
                                    title: 'Create Embedding Model',
                                    description: 'Define a remote model in Spanner that points to a pre-trained text embedding service, like Google\'s text-embedding-004.'
                                },
                                {
                                    number: 2,
                                    title: 'Add Embedding Column',
                                    description: 'Add a new column to your table to store the generated embeddings as an array of FLOAT32 values.'
                                },
                                {
                                    number: 3,
                                    title: 'Generate Embeddings',
                                    description: 'Use ML.PREDICT to populate the embedding column for existing data, generating vector representations for record.'
                                },
                                {
                                    number: 4,
                                    title: 'Test Semantic Search',
                                    description: 'Perform similarity searches using cosine distance to find the most semantically relevant items based on a query embedding.'
                                }
                            ].map((step, index) => (
                                <div className="col-lg-6 col-md-6 col-sm-12" key={index} style={{ marginBottom: '20px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={300 + index * 100}>
                                        <div className="mmai-step-card">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                                                <div className="mmai-step-number">
                                                    {step.number}
                                                </div>
                                                <h4 className="mmai-step-title" style={{ margin: 0 }}>
                                                    {step.title}
                                                </h4>
                                            </div>
                                            <p className="mmai-step-desc" style={{ margin: 0 }}>
                                                {step.description}
                                            </p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Graph RAG Agent Segment */}
                <div className="rn-section-gap mmai-section-dark">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6 col-md-12 mb-4 mb-lg-0">
                                <ScrollAnimation animateIn="fadeInRight" animateOnce={true}>
                                    <img 
                                        src="/images/bg/GraphRAGAgent.png"
                                        style={{ 
                                                width: '60%', 
                                                maxWidth: '400px',
                                                borderRadius: '40px', 
                                                boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                border: '1px solid rgba(255,255,255,0.05)',
                                                display: 'block',
                                                marginLeft: 'auto',
                                                marginRight: 'auto'
                                            }} 
                                    />
                                </ScrollAnimation>
                            </div>
                            <div className="col-lg-6 col-md-12">
                                <ScrollAnimation animateIn="fadeInLeft" animateOnce={true}>
                                    <h2 className="mmai-graph-rag-title" style={{ fontSize: '36px', marginBottom: '30px', lineHeight: '1.2' }}>
                                        Graph RAG Agent
                                    </h2>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        {[
                                            {
                                                icon: <FiLayers style={{ fontSize: '20px' }} />,
                                                title: 'Semantic (RAG)',
                                                description: 'Use for conceptual queries, cosine distance on 768 dim vectors.'
                                            },
                                            {
                                                icon: <FiCheck style={{ fontSize: '20px' }} />,
                                                title: 'Keyword',
                                                description: 'Use for filters and exact matches via SQL LIKE clauses.'
                                            },
                                            {
                                                icon: <FiGitBranch style={{ fontSize: '20px' }} />,
                                                title: 'Hybrid (RRF)',
                                                description: 'Combine ranks with Reciprocal Rank Fusion to merge keyword + semantic results.'
                                            }
                                        ].map((item, index) => (
                                            <div key={index} style={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start', 
                                                gap: '15px'
                                            }}>
                                                <div className="mmai-graph-rag-item-icon">
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <h4 className="mmai-graph-rag-item-title" style={{ marginBottom: '5px' }}>
                                                        {item.title}
                                                    </h4>
                                                    <p className="mmai-graph-rag-item-desc" style={{ margin: 0 }}>
                                                        {item.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Multimodal Pipeline & Agents Segment */}
                <div className="rn-section-gap mmai-section">
                    <div className="container">
                        <div className="row mb-5">
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                    <h2 className="mmai-graph-rag-title" style={{ fontSize: '34px', marginBottom: '10px' }}>
                                        Multimodal Pipeline & Agents
                                    </h2>
                                </ScrollAnimation>
                            </div>
                        </div>
                        <div className="row align-items-center">
                            <div className="col-lg-6 col-md-12 mb-4 mb-lg-0">
                                <ScrollAnimation animateIn="fadeInLeft" animateOnce={true} delay={100}>
                                    <h3 className="mmai-sequential-title">
                                        Sequential Agent Flow
                                    </h3>
                                    <ul style={{ listStyle: 'none', padding: '0', margin: '0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {[
                                            'Upload Agent → GCS URI',
                                            'Extraction Agent → Gemini Vision → JSON entities',
                                            'Spanner Agent → persist nodes & edges',
                                            'Summary Agent → user-friendly report',
                                            'Interactive error handling and LLM-driven next steps.'
                                        ].map((item, index) => (
                                            <li key={index} className="mmai-sequential-item">
                                                <span className="mmai-sequential-dot"></span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </ScrollAnimation>
                            </div>
                            <div className="col-lg-6 col-md-12">
                                <ScrollAnimation animateIn="fadeInRight" animateOnce={true}>
                                    <img 
                                        src="/images/bg/MultimodalPipeline.png"
                                        style={{ 
                                                width: '60%', 
                                                maxWidth: '400px',
                                                borderRadius: '40px', 
                                                boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 60px rgba(101, 73, 213, 0.15)',
                                                border: '1px solid rgba(255,255,255,0.05)',
                                                display: 'block',
                                                marginLeft: 'auto',
                                                marginRight: 'auto'
                                            }}
                                    />
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />

                {/* Memory Bank & Personalization Section */}
                <div className="rn-section-gap mmai-section">
                    <div className="container">
                        <div className="row mb-5">
                            <div className="col-lg-12 text-center mb-5">
                                <h2 className="mmai-memory-section-title" style={{ fontSize: '42px', marginBottom: '10px' }}>
                                    Memory Bank & Personalization
                                </h2>
                            </div>
                        </div>
                        <div className="row align-items-stretch">
                            {[
                                {
                                    icon: <FiClock />,
                                    title: 'Short Term',
                                    description: 'In Memory Session Service for current optional conversation context.'
                                },
                                {
                                    icon: <FiDatabase />,
                                    title: 'Long Term',
                                    description: 'Vertex Ai Memory Bank Service stores user preferences and recurring needs.'
                                },
                                {
                                    icon: <FiTag />,
                                    title: 'Memory Topics',
                                    description: 'Search_preferences, urgent_needs_context for targeted personalization.'
                                }
                            ].map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-sm-12" key={index} style={{ marginBottom: '20px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                        <div className="mmai-memory-card">
                                            <div className="mmai-memory-icon">
                                                {item.icon}
                                            </div>
                                            <h3 className="mmai-memory-title">
                                                {item.title}
                                            </h3>
                                            <p className="mmai-memory-desc">
                                                {item.description}
                                            </p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* What We Built — Key Outcomes */}
                <div className="rn-section-gap" style={{ 
                    background: 'linear-gradient(135deg, #0f0f11, #1a1a2e)',
                    borderRadius: '40px',
                    padding: '80px 30px',
                    margin: '0 20px',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative gradient background elements */}
                    <div style={{ 
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        background: 'radial-gradient(circle at 30% 20%, rgba(101, 73, 213, 0.2) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(227, 63, 161, 0.2) 0%, transparent 50%)',
                        zIndex: '0'
                    }}></div>
                    
                    <div className="container" style={{ position: 'relative', zIndex: '1' }}>
                        <div className="row mb-5">
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                    <h2 style={{ 
                                        fontSize: '48px', 
                                        color: '#fff', 
                                        fontWeight: '700', 
                                        marginBottom: '50px',
                                        background: 'linear-gradient(90deg, #fff, #d4cce8)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}>
                                        What We Built Key Outcomes
                                    </h2>
                                </ScrollAnimation>
                            </div>
                        </div>
                        <div className="row">
                            {[
                                {
                                    title: 'Graph Database',
                                    description: 'Spanner with nodes & edges for practitioners, specialisms, seeks, materials.'
                                },
                                {
                                    title: 'AI Search',
                                    description: 'Keyword, semantic & hybrid search with 768-dim embeddings.'
                                },
                                {
                                    title: 'Multimodal & Agents',
                                    description: 'Gemini Vision extraction, ADK orchestration, sequential agent pipelines.'
                                },
                                {
                                    title: 'Memory & Deploy',
                                    description: 'Vertex AI Memory Bank for personalization; Cloud Run production deploy.'
                                }
                            ].map((item, index) => (
                                <div className="col-lg-6 col-md-6 col-sm-12" key={index} style={{ marginBottom: '20px' }}>
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 100}>
                                        <div style={{ 
                                            background: 'linear-gradient(135deg, rgba(58, 43, 125, 0.9), rgba(26, 14, 69, 0.9))',
                                            padding: '35px 30px',
                                            borderRadius: '12px',
                                            height: '100%',
                                            border: '1px solid rgba(101, 73, 213, 0.4)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-8px)';
                                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(101,73,213,0.25)';
                                            e.currentTarget.style.borderColor = 'rgba(101, 73, 213, 0.7)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                            e.currentTarget.style.borderColor = 'rgba(101, 73, 213, 0.4)';
                                        }}>
                                            <h3 style={{ 
                                                fontSize: '28px', 
                                                fontWeight: '700', 
                                                color: '#fff', 
                                                marginBottom: '12px',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {item.title}
                                            </h3>
                                            <p style={{ 
                                                color: '#d4cce8', 
                                                fontSize: '18px', 
                                                margin: '0', 
                                                lineHeight: '1.6'
                                            }}>
                                                {item.description}
                                            </p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* How It Works Section - Horizontal Timeline Layout */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <SectionTitle
                                    textAlign="text-center"
                                    radiusRounded=""
                                    subtitle="IMPLEMENTATION"
                                    title="How It Works"
                                    description="A smooth, three-phase process to get your multimodal AI up and running."
                                />
                            </div>
                        </div>
                        <div className="row mt--50" style={{ justifyContent: 'center' }}>
                            {ProcessData.map((item, index) => (
                                <div className="col-lg-4 col-md-6 col-12" key={index} style={{ marginBottom: '30px', position: 'relative' }}>
                                    {index < ProcessData.length - 1 && (
                                        <div className="timeline-connector"></div>
                                    )}
                                    <ScrollAnimation animateIn="fadeInUp" animateOnce={true} delay={index * 150}>
                                        <div className="mmai-timeline-card">
                                            <div className="mmai-timeline-number">
                                                {index + 1}
                                            </div>
                                            <div className="mmai-timeline-step">
                                                {item.step}
                                            </div>
                                            <h4 className="mmai-timeline-title">{item.title}</h4>
                                            <p className="mmai-timeline-desc">{item.description}</p>
                                        </div>
                                    </ScrollAnimation>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <Separator />

                {/* CTA Section - Modern AI Vibe */}
                <div className="rn-section-gap">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-12">
                                <ScrollAnimation animateIn="fadeInUp" animateOnce={true}>
                                    <div className="mmai-cta-container">
                                        <div className="mmai-cta-bg"></div>
                                        <div className="mmai-cta-content">
                                            <h2 className="mmai-cta-title">
                                                Ready to Experience the Future?
                                            </h2>
                                            <p className="mmai-cta-desc">
                                                Let's discuss how WhatsOn Multimodal AI Agent can transform your data analysis and content creation.
                                            </p>
                                            <div className="button-group" style={{ justifyContent: 'center', display: 'flex', width: '100%' }}>
                                                <Link className="btn-default btn-medium round btn-icon" to="/contact" style={{ 
                                                    background: '#fff', 
                                                    color: '#6549D5',
                                                    border: 'none',
                                                    fontWeight: '700',
                                                    boxShadow: '0 15px 35px rgba(0,0,0,0.25)',
                                                    fontSize: '16px',
                                                    padding: '15px 35px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}>
                                                    Get Started
                                                    <i className="icon">
                                                        <FiArrowRight />
                                                    </i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </ScrollAnimation>
                            </div>
                        </div>
                    </div>
                </div>

                <Separator />
               
                <FooterTwo />
                <Copyright />
            </main>
        </>
    )
}

export default MultimodelAiAgent;
